/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Forum as ForumIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useDebate } from '../contexts/DebateContext';
import { Debate } from '../types';
import logger from '../utils/logger';

const PublicDebatesPage: React.FC = () => {
  const { t } = useTranslation();
  const { 
    activeDebates, 
    completedDebates,
    debouncedFetchActiveDebates, 
    debouncedFetchCompletedDebates,
    loading, 
    error 
  } = useDebate();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredActive, setFilteredActive] = useState<Debate[]>([]);
  const [filteredCompleted, setFilteredCompleted] = useState<Debate[]>([]);
  
  // Use a ref to track whether we've already done the initial data fetch
  const initialFetchDoneRef = React.useRef(false);

  // Fetch debates on component mount - only once
  useEffect(() => {
    // Only fetch if we haven't already done the initial fetch
    if (!initialFetchDoneRef.current) {
      logger.info('PublicDebatesPage: Performing initial debates fetch');
      debouncedFetchActiveDebates();
      debouncedFetchCompletedDebates();
      initialFetchDoneRef.current = true; // Mark initial fetch as done
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array with ESLint exception to prevent infinite loop
  
  // Filter debates based on search term
  useEffect(() => {
    if (activeDebates) {
      // Only include public debates
      setFilteredActive(
        activeDebates.filter(debate => 
          debate.isPublic === true && (
            debate.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (debate.description && debate.description.toLowerCase().includes(searchTerm.toLowerCase()))
          )
        )
      );
    } else {
      setFilteredActive([]);
    }
    
    if (completedDebates) {
      // Only include public debates
      setFilteredCompleted(
        completedDebates.filter(debate => 
          debate.isPublic === true && (
            debate.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (debate.description && debate.description.toLowerCase().includes(searchTerm.toLowerCase()))
          )
        )
      );
    } else {
      setFilteredCompleted([]);
    }
  }, [searchTerm, activeDebates, completedDebates]);
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  // Render a debate card
  const renderDebateCard = (debate: Debate) => {
    return (
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" component="h2" gutterBottom noWrap>
              {debate.topic}
            </Typography>
            <Chip
              label={debate.status.toUpperCase()}
              size="small"
              color={
                debate.status === 'active'
                  ? 'success'
                  : debate.status === 'completed'
                  ? 'default'
                  : 'primary'
              }
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('created')}: {formatDate(debate.createdAt)}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" sx={{ mb: 2 }}>
            {debate.description || t('noDescriptionProvided')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('teams')}: {debate.teams.map((team) => team.name).join(' vs. ')}
          </Typography>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
          {debate.status === 'active' ? (
            <>
              <Button
                component={RouterLink}
                to={`/debate/view/${debate.id}`}
                size="small"
                color="info"
                startIcon={<VisibilityIcon />}
              >
                {t('view')}
              </Button>
              {debate.publicJoinable ? (
                <Button
                  component={RouterLink}
                  to={`/debate/view/${debate.id}?join=true`}
                  size="small"
                  color="primary"
                  endIcon={<ForumIcon />}
                >
                  {t('join')}
                </Button>
              ) : (
                <Button
                  component={RouterLink}
                  to={`/debate/view/${debate.id}`}
                  size="small"
                  color="secondary"
                  startIcon={<VisibilityIcon />}
                  disabled
                >
                  {t('viewOnly')}
                </Button>
              )}
            </>
          ) : (
            <Button
              component={RouterLink}
              to={`/debate/view/${debate.id}`}
              size="small"
              color="primary"
              endIcon={<ForumIcon />}
            >
              {t('viewResults')}
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  // Add a refresh function
  const refreshDebates = () => {
    logger.info('Manually refreshing debates...');
    debouncedFetchActiveDebates();
    debouncedFetchCompletedDebates();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          {t('publicDebates')}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={refreshDebates}
          startIcon={<RefreshIcon />}
        >
          {t('refresh')}
        </Button>
      </Box>
      
      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder={t('searchDebates')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>
      
      {/* Active Debates Section */}
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        {t('activeDebates')}
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : filteredActive.length === 0 ? (
        <Paper elevation={0} variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {t('noActivePublicDebates')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {filteredActive.map((debate) => (
            <Grid item xs={12} md={6} key={debate.id}>
              {renderDebateCard(debate)}
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Completed Debates Section */}
      <Divider sx={{ mb: 4 }} />
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        {t('completedDebates')}
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : filteredCompleted.length === 0 ? (
        <Paper elevation={0} variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('noCompletedPublicDebates')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredCompleted.map((debate) => (
            <Grid item xs={12} md={6} key={debate.id}>
              {renderDebateCard(debate)}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default PublicDebatesPage; 